import { useTranslation } from 'react-i18next';
import {
    Box,
    Link,
    Grid,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { getPath } from '../../../pages/Pages';
import { formatPriceByLocale, formatPeriod } from '../../../lib/utils';

type Activity = {
    id: number;
    name: string;
    sport: {
        name: string;
    };
    sc: {
        id: number;
        name: string;
    };
    ini: string;
    end: string;
    inscIni: string;
    inscEnd: string;
    price: number;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
    numStudents: number;
    maxStudents: number;
};

type Props = {
    activity?: Activity;
};

/**
 * Card representing an activity.
 * @param activity Activity object.
 * @returns {React.ReactElement|null}
 */
export function SttActivityCard({
    activity,
}: Props): React.ReactElement | null {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    if (!activity) {
        return null;
    }

    // Is the activity "in time"?
    const now = new Date();
    const inTime =
        isAfter(now, parseISO(activity.ini)) &&
        isBefore(now, parseISO(activity.end));
    const inIscTime =
        isAfter(now, parseISO(activity.inscIni)) &&
        isBefore(now, parseISO(activity.inscEnd));

    const dateFormat = mdUp ? 'D MMMM YYYY' : 'DD/MM/YYYY';
    const [period, , periodLabel] = formatPeriod(
        activity.ini,
        activity.end,
        { dateFormat },
        t,
    );
    const [inscPeriod] = formatPeriod(
        activity.inscIni,
        activity.inscEnd,
        { dateFormat },
        t,
    );

    return (
        <Box p={3}>
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 10 }}>
                    <div>
                        <Link
                            onClick={() =>
                                navigate(
                                    getPath('activityPublic', {
                                        state: {
                                            id: activity.id,
                                        },
                                    }),
                                )
                            }
                        >
                            <Typography variant="h5">
                                {activity.name}
                            </Typography>
                        </Link>
                    </div>
                    <div>
                        <Link
                            onClick={() =>
                                navigate(
                                    getPath('timetable', {
                                        state: {
                                            id: activity.sc.id,
                                        },
                                    }),
                                )
                            }
                        >
                            <Typography variant="body2">
                                {activity.sc.name}
                            </Typography>
                        </Link>
                    </div>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <Box textAlign={mdUp ? 'right' : 'left'}>
                        <Typography variant="button">
                            {activity.sport.name}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Typography variant="body1">
                        <span>{periodLabel}: </span>
                        <span className={inTime ? '' : ''}>{period}</span>{' '}
                        {/* Change the color based on wether the activity is "in time" */}
                    </Typography>
                    <Typography variant="body1">
                        <span>{t('Inscription')}: </span>
                        <span className={inIscTime ? '' : ''}>
                            {inscPeriod}
                        </span>{' '}
                        {/* Change the color based on wether the inscription period is "in time" */}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" justifyContent="space-between">
                        <Box>
                            <Typography variant="body2">
                                {t('Price')}
                            </Typography>
                            <Typography variant="body1">{`${t(
                                'From',
                            )} ${formatPriceByLocale(
                                activity.price,
                                activity?.priceShape?.currency,
                                activity?.priceShape?.locale,
                            )}`}</Typography>
                        </Box>
                        <Box textAlign="right">
                            <Typography variant="body2">
                                {t('Students')}
                            </Typography>
                            <Typography variant="body1">{`${
                                activity.numStudents
                            } / ${
                                activity.maxStudents > 0
                                    ? activity.maxStudents
                                    : '∞'
                            }`}</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
